import { useState, useEffect, useRef } from 'react';

// Custom hook for WebSocket connection with improved debugging
export const useWebSocket = (strategyId, onUpdate) => {
  // Add this debugging flag to enable/disable verbose logging
  const DEBUG_MODE = true; // Set to false when you're done debugging
  const messageCount = useRef(0); // Track message count for debugging
  
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const keepaliveRef = useRef(null);
  const connectAttempts = useRef(0);
  const MAX_RECONNECT_ATTEMPTS = 10; // Increased for better reliability
  const INITIAL_RECONNECT_DELAY = 2000; // 2 seconds - faster initial reconnect
  const MAX_RECONNECT_DELAY = 30000; // 30 seconds - reduced max delay
  // const lastMessageTimeRef = useRef(0); // Removed - not needed with enhanced debugging
  const messageBuffer = useRef([]); // Buffer messages during disconnections
  const lastConnectionAttempt = useRef(0);
  const MIN_RECONNECT_INTERVAL = 1000; // Minimum 1 second between connection attempts
  const onUpdateRef = useRef(onUpdate); // Store callback in ref to avoid reconnections
  
  // Update the ref when callback changes without causing reconnection
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);
  
  // Add this debugging log function
  const debugLog = (message, data) => {
    if (!DEBUG_MODE) return;
    
    if (data) {
      console.log(`[WebSocket Debug] ${message}`, data);
    } else {
      console.log(`[WebSocket Debug] ${message}`);
    }
  };
  
  useEffect(() => {
    // Only attempt to connect if we have a strategy ID and callback
    if (!strategyId || !onUpdate) {
      debugLog(`No strategyId or onUpdate callback, skipping connection`);
      return;
    }
    
    // Handle page visibility changes to ensure WebSocket stays connected
    const handleVisibilityChange = () => {
      if (document.hidden) {
        debugLog('Page hidden - keeping WebSocket connection alive');
      } else {
        debugLog('Page visible - WebSocket should be active');
        // If connection was lost while hidden, reconnect
        if (wsRef.current && wsRef.current.readyState !== WebSocket.OPEN) {
          debugLog('Reconnecting WebSocket after page became visible');
          connect();
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Clear any existing connection
    if (wsRef.current) {
      debugLog('Cleaning up previous connection');
      clearTimeout(reconnectTimeoutRef.current);
      
      if (wsRef.current.readyState === WebSocket.OPEN || 
          wsRef.current.readyState === WebSocket.CONNECTING) {
        // Only log if actually connected
        debugLog('Closing existing connection');
        
        try {
          wsRef.current.close(1000, 'Normal closure');
        } catch (err) {
          // Ignore errors during cleanup
          debugLog('Error during connection cleanup', err);
        }
      }
      
      wsRef.current = null;
    }
    
    // Reset connection attempts
    connectAttempts.current = 0;
    
    const connect = () => {
      debugLog(`Connect called for strategy ${strategyId}`);
      
      // Don't attempt to reconnect if we already have an open connection
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        debugLog('Already connected, skipping reconnect');
        return;
      }
      
      // Prevent too frequent reconnection attempts
      const now = Date.now();
      if (now - lastConnectionAttempt.current < MIN_RECONNECT_INTERVAL) {
        debugLog('Connection attempt too soon, waiting...');
        setTimeout(connect, MIN_RECONNECT_INTERVAL);
        return;
      }
      lastConnectionAttempt.current = now;
      
      // If we've tried too many times, increase delay but keep trying
      if (connectAttempts.current >= MAX_RECONNECT_ATTEMPTS) {
        debugLog(`Max reconnect attempts (${MAX_RECONNECT_ATTEMPTS}) reached, using longer delays`);
        setError('Connection unstable - retrying with longer intervals');
      }
      
      // Only increment attempts when we're actually trying (not on initial connect)
      if (connectAttempts.current > 0) {
        debugLog(`Reconnect attempt ${connectAttempts.current + 1}`);
      }
      connectAttempts.current++;
      
      try {
        // Check if the strategy ID is still valid before connecting
        if (!strategyId) {
          debugLog('No strategy ID available, skipping connection');
          return;
        }
        
        // Check if the strategy ID is actually an external ID (common mistake)
        const isExternalId = strategyId && strategyId.length > 20 && !strategyId.match(/^\d+$/);
        const connectionId = isExternalId ? strategyId : strategyId;
        
        // Create WebSocket connection to the real agent backend
        // FIX: Use wss:// protocol for secure connections instead of ws://
        // Also determine protocol based on current page protocol
        const protocol = window.location.protocol === 'https:' ? 'wss://' : 'ws://';
        const hostname = 'token-ai-agent-d09010489214.herokuapp.com';
        const wsUrl = `${protocol}${hostname}/ws/logs/${connectionId}`;
        
        debugLog(`Connecting to ${wsUrl}`, { attempt: connectAttempts.current, connectionId });
        
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        
        // Add a connection timeout
        const connectionTimeout = setTimeout(() => {
          // Only take action if the connection is still connecting
          if (ws.readyState === WebSocket.CONNECTING) {
            debugLog('Connection attempt timed out');
            ws.close();
          }
        }, 10000); // 10 second connection timeout
        
        // Add event listeners
        ws.onopen = () => {
          clearTimeout(connectionTimeout); // Clear timeout on successful connection
          debugLog('Connected successfully to trading agent');
          setConnected(true);
          setError(null);
          connectAttempts.current = 0; // Reset counter on successful connection
          
          // Send any buffered messages
          if (messageBuffer.current.length > 0) {
            debugLog(`Processing ${messageBuffer.current.length} buffered messages`);
            messageBuffer.current.forEach(message => {
              if (onUpdateRef.current) {
                onUpdateRef.current(message);
              }
            });
            messageBuffer.current = [];
          }
          
          // Start keepalive to maintain connection
          keepaliveRef.current = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
              try {
                ws.send(JSON.stringify({ type: 'ping' }));
              } catch (e) {
                debugLog('Keepalive ping failed:', e);
              }
            }
          }, 30000); // Ping every 30 seconds
        };
        
        ws.onclose = (event) => {
          clearTimeout(connectionTimeout); // Clear timeout if it wasn't cleared yet
          clearInterval(keepaliveRef.current); // Stop keepalive
          
          // Normal closure of server shutdown shouldn't trigger a message
          if (event.code === 1000 || event.code === 1001) {
            debugLog(`Normal closure, code: ${event.code}, reason: ${event.reason || 'No reason provided'}`);
          } else {
            debugLog(`Disconnected, code: ${event.code}, reason: ${event.reason || 'No reason provided'}`);
          }
          
          // Only change UI state if we were previously connected
          if (connected) {
            setConnected(false);
          }
          
          // Only attempt to reconnect on abnormal closure, not when we close it intentionally
          if (event.code !== 1000 && event.code !== 1001) {
            // More aggressive exponential backoff for unstable connections
            let delay;
            if (connectAttempts.current < 3) {
              delay = INITIAL_RECONNECT_DELAY; // Quick retry for first few attempts
            } else {
              delay = Math.min(
                INITIAL_RECONNECT_DELAY * Math.pow(2, Math.min(connectAttempts.current - 3, 5)) + 
                (Math.random() * 1000), // Add jitter
                MAX_RECONNECT_DELAY
              );
            }
            
            debugLog(`Will attempt to reconnect in ${Math.round(delay)}ms (attempt ${connectAttempts.current})`);
            reconnectTimeoutRef.current = setTimeout(connect, delay);
          }
        };
        
        ws.onerror = (error) => {
          clearTimeout(connectionTimeout); // Clear timeout on error
          debugLog('Error occurred', error);
          
          // If we get an error, increment connection attempts to trigger fallback sooner
          // but don't show errors to the user early in the connection process
          if (connectAttempts.current >= MAX_RECONNECT_ATTEMPTS / 2) {
            setError('WebSocket connection error - retrying...');
          }
        };
        
        ws.onmessage = (event) => {
          try {
            messageCount.current++;
            
            // Always log the raw message for debugging
            debugLog(`RAW MESSAGE #${messageCount.current} RECEIVED:`, event.data);
            
            // Skip ping/pong messages
            try {
              const parsed = JSON.parse(event.data);
              if (parsed.type === 'pong' || parsed.type === 'ping') {
                debugLog(`Skipping keepalive message: ${parsed.type}`);
                return; // Skip keepalive messages
              }
            } catch (e) {
              // Not JSON, continue processing
              debugLog(`Message is not JSON, processing as plain text`);
            }
            
            // Always log message processing for debugging
            debugLog(`Processing WebSocket message #${messageCount.current}, callback available: ${!!onUpdateRef.current}`);
            
            // Send the original event.data to the callback so terminal can decide what to show
            // This ensures ALL external API data reaches the terminal component
            if (onUpdateRef.current) {
              debugLog(`Calling onUpdate with message #${messageCount.current}`);
              try {
                onUpdateRef.current(event.data); // Send raw data
                debugLog(`Successfully called onUpdate for message #${messageCount.current}`);
              } catch (callbackError) {
                console.error(`Error in onUpdate callback for message #${messageCount.current}:`, callbackError);
              }
            } else {
              debugLog(`No onUpdate callback provided, buffering message #${messageCount.current}`);
              // Buffer the message if no callback is available yet
              messageBuffer.current.push(event.data);
              // Keep buffer size reasonable
              if (messageBuffer.current.length > 100) {
                messageBuffer.current = messageBuffer.current.slice(-50);
              }
              debugLog(`Message buffer now contains ${messageBuffer.current.length} messages`);
            }
          } catch (error) {
            console.error(`WebSocket: Error handling message #${messageCount.current}`, error);
          }
        };
      } catch (err) {
        debugLog('Error creating connection', err);
        
        // Retry connection after delay
        const delay = Math.min(
          INITIAL_RECONNECT_DELAY * Math.pow(1.5, connectAttempts.current),
          MAX_RECONNECT_DELAY
        );
        reconnectTimeoutRef.current = setTimeout(connect, delay);
      }
    };
    
    // Start connection
    connect();
    
    // Cleanup function
    return () => {
      debugLog(`Cleaning up WebSocket connection for strategy ${strategyId}`);
      clearTimeout(reconnectTimeoutRef.current);
      clearInterval(keepaliveRef.current);
      
      // Remove visibility change listener
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      if (wsRef.current) {
        try {
          debugLog(`Closing WebSocket in cleanup, readyState: ${wsRef.current.readyState}`);
          wsRef.current.close(1000, 'Component unmounting');
        } catch (err) {
          debugLog(`Error during WebSocket cleanup:`, err);
        }
        
        wsRef.current = null;
      }
      
      // Clear message buffer
      messageBuffer.current = [];
    };
  }, [strategyId]); // Remove onUpdate from dependencies to prevent reconnections
  
  // This hook provides:
  // - connected: boolean indicating if WebSocket is connected
  // - error: any connection error
  return { 
    connected, 
    error,
    // Add this debug info
    debug: {
      attempts: connectAttempts.current,
      wsState: wsRef.current ? wsRef.current.readyState : 'no connection',
      strategyId
    }
  };
};

export default useWebSocket;